import { Paper } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getPaymentMethodLabel } from '../../../hooks/usePaymentus';
import { useTranslation } from '../../../hooks/useTranslation';
import { dateSuffixMap, toDateString } from '../../../util/format';
import {
  AccountDetail,
  AccountType,
  ListProfilesResponse,
  PaymentCategory,
  PaymentProfile,
} from '../../../__generated__/pge-types';
import PGEButton from '../../buttons';
import DaySelector from '../../day-selector';
import PaymentMethodSelector from '../../payment-wallet/payment-method-selector';
import PaymentLimitsFees from '../../paymentus/payment-limits-fees';
import { ADD_PROFILE, EditProps } from '../../paymentus/types';
import {
  getAccountDisplayString,
  getPmCategoryFromType,
} from '../../paymentus/utils';
import { AutoPayConfigData } from '../auto-pay.types';
import {
  getDayFromDueDate,
  getPaymentMethodSelectorData,
  isDateInFuture,
  isDateInNextNDays,
  isEligibleToCancel,
  isEligibleToChangePaymentMethod,
  isPDDEligible,
  mergeAutoPayConfigChanges,
} from '../auto-pay.utils';
import { useStyles } from './AutoPayStart.styles';
import IneligibleAlert from './IneligibleAlert';
import AutoPayMethodChangeDialog from '../auto-pay-method-change-dialog/AutoPayMethodChangeDialog';
import PaymentProfileRemoveDialog from '../../payment-form/payment-profile-remove/PaymentProfileRemove';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import EditPaymentProfile from '../../paymentus/edit-payment-profile';
import { ApolloQueryResult } from 'apollo-client';
import PaymentCardExpirationModal from '../payment-card-expiration-modal/PaymentCardExpirationModal';
import { isIphoneChrome } from '../../../util/mobile/iphoneBrowser-validate';

type Props = {
  onUpdatePaymentProfile: (
    config: AutoPayConfigData,
    isModal: boolean,
  ) => Promise<void>;
  savedProfileList?: PaymentProfile[];
  onUpdatePayDay: (config: AutoPayConfigData) => Promise<void>;
  onUpdateAutoPayConfig: (config: AutoPayConfigData) => Promise<void>;
  onCancel: () => Promise<void>;
  account: AccountDetail;
  onAutoPayConfirmSubmit?: (isAmazonUpdate?: boolean) => void;
  setIneligibleValue?: (value: boolean) => void;
  setShowAmazonModal?: (value: boolean) => void;
  personId?: string;
  refetch?: () => Promise<
    [
      ApolloQueryResult<{
        listProfiles: ListProfilesResponse;
      }>,
      ApolloQueryResult<{
        getAccountDetails: Array<AccountDetail>;
      }>,
    ]
  >;
  showAmazonModal?: boolean;
};

type AUTO_PAY_OPTIONS =
  | 'AUTO_PAY_UPDATE_PAYMENT_PROFILE'
  | 'AUTO_PAY_UPDATE_DAY'
  | 'AUTO_PAY_CANCEL';

export default function AutoPayEnrolled({
  onUpdatePayDay,
  onUpdatePaymentProfile,
  savedProfileList,
  onUpdateAutoPayConfig,
  onCancel,
  account,
  onAutoPayConfirmSubmit,
  setIneligibleValue,
  setShowAmazonModal,
  showAmazonModal,
  personId,
  refetch,
}: Props) {
  const classes = useStyles();
  const { t, richT } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<
    AUTO_PAY_OPTIONS | undefined
  >();

  const [autoNext, setAutoNext] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const currentPaymentProfile = savedProfileList?.find(
    p => p.token === account?.autoPay?.enrollDetails?.tokenId,
  );

  const preferredDueDate =
    account?.preferredDueDate?.dueDate?.preferredDueDate ||
    (isDateInFuture(account?.currentCharges?.dueDate)
      ? getDayFromDueDate(account?.currentCharges?.dueDate)
      : null);

  const defaultAutoPayConfig = useMemo(
    () => ({
      selectedDay: preferredDueDate,
      paymentSelector: getPaymentMethodSelectorData(
        'autoPay',
        account?.paymentEligibility,
        account?.paymentAlerts?.isFutureDated,
        savedProfileList,
        currentPaymentProfile ? { profile: currentPaymentProfile } : undefined,
      ),
    }),
    [account],
  );
  const [editProps, setEditProps] = useState<EditProps>();
  const [showEdit, setShowEdit] = useState(false);
  const [
    showPaymentCardExpirationModal,
    setShowPaymentCardExpirationModal,
  ] = useState(false);
  const notificationContext = useContext(NotificationsContext);
  const [autoPayConfig, setAutoPayConfig] = useState<AutoPayConfigData>(
    defaultAutoPayConfig,
  );
  const { wrapWithLoader } = useWrapWithLoader();

  const handleEditProfileDone = wrapWithLoader(
    async (profile: PaymentProfile) => {
      await refetch!();
      setAutoPayConfig(config =>
        mergeAutoPayConfigChanges(config, {
          newProfileList: [...config.paymentSelector.newProfileList, profile],
          selectedProfileInfo: {
            ...config.paymentSelector.selectedProfileInfo,
            profile: profile,
          },
        }),
      );
      setShowPaymentCardExpirationModal(false);
    },
  );
  const handleEditProfile = (profile: PaymentProfile) => {
    const onClose = () => {
      setShowEdit(false);
      setEditProps(undefined);
    };

    setEditProps({
      iframeProps: {
        ownerId: personId || '-1',
        token: profile.token,
        pmCategory: getPmCategoryFromType(profile.type)!,
        postMessagePmDetailsOrigin: location.origin,
      },
      onChange: async (changedProfile: PaymentProfile) => {
        await handleEditProfileDone(changedProfile);
        onClose();
        notificationContext.setState({
          isOpen: true,
          severity: 'success',
          variant: 'filled',
          message: t('PAYMENT_PROFILE_EDIT_SUCCESS_MESSAGE'),
        });
      },
      onClose,
    });
    setShowEdit(true);
  };
  useEffect(() => {
    if (currentPaymentProfile?.type) {
      if (currentPaymentProfile?.type === PaymentCategory.AmazonPay) {
        setShowModal(true);
      }
    }
    if (
      currentPaymentProfile?.usedForAutopay &&
      isEligibleToChangePaymentMethod(account.autoPay)
    ) {
      // Check if the profile is expiring within 3 months

      const expMonth = Number(
        currentPaymentProfile?.creditCardExpiryDate?.month,
      );
      const expYear = Number(currentPaymentProfile?.creditCardExpiryDate?.year);
      if (expMonth && expYear) {
        const today = new Date();
        const expiryDate = new Date(expYear, expMonth - 1, 1);
        // Add 1 month to expiryDate to get the last day of the expiry month
        expiryDate.setMonth(expiryDate.getMonth() + 1);
        expiryDate.setDate(0); // last day of previous month
        const threeMonthsFromNow = new Date(today);
        threeMonthsFromNow.setMonth(today.getMonth() + 3);

        if (expiryDate <= threeMonthsFromNow) {
          setShowPaymentCardExpirationModal(true);
        }
      }
    }
  }, [account]);

  // const bankSectionForm = useBankSectionForm(autoPayConfig.paymentSelector);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoPayConfig(defaultAutoPayConfig);
    setSelectedOption(
      (event.target as HTMLInputElement).value as AUTO_PAY_OPTIONS,
    );
  };

  async function handlePaymentProfileChange() {
    try {
      if (
        autoPayConfig.paymentSelector.selectedProfileInfo?.profile?.type ===
        PaymentCategory.AmazonPay
      ) {
        window.scrollTo(0, 0);
        setIneligibleValue?.(true);
        return;
      }
      return onUpdatePaymentProfile(autoPayConfig, showModal);
    } catch (_) {
      // Do nothing, this means, there was a form error
    }
  }

  useEffect(() => {
    if (autoNext) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handlePaymentProfileChange();
    }
  }, [autoNext]);

  const typeLabel = getPaymentMethodLabel(currentPaymentProfile?.type, true);

  const enrolledLabel = currentPaymentProfile?.type
    ? `${t('AUTO_PAY_ENROLLED_INFO_PAYMENT_PROFILE')} with ${typeLabel} `
    : t('AUTO_PAY_ENROLLED_INFO_OTHER');
  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Card className={classes.root}>
          <CardContent className={classes.cardContent}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant={'h4'}>
                  {enrolledLabel}
                  {!!currentPaymentProfile?.type && (
                    <b>{getAccountDisplayString(currentPaymentProfile)}</b>
                  )}
                  .
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {preferredDueDate ? (
                  <Typography variant={'body1'}>
                    {t('AUTO_PAY_ENLROLLED_INFO_DAY')}
                    <strong>
                      {preferredDueDate}
                      {dateSuffixMap(t, preferredDueDate)}
                    </strong>
                    .
                  </Typography>
                ) : (
                  <Typography variant={'body1'}>
                    {t('AUTO_PAY_ENROLLED_DUE_DAY')}
                  </Typography>
                )}
              </Grid>
              <Grid item container direction={'column'} xs={12}>
                <RadioGroup
                  aria-label="change-auto-pay"
                  value={selectedOption || ''}
                  onChange={handleOptionChange}
                >
                  <FormControlLabel
                    value={'AUTO_PAY_UPDATE_PAYMENT_PROFILE'}
                    control={<Radio color={'primary'} />}
                    label={t('AUTO_PAY_ENROLLED_UPDATE_PAYMENT_PROFILE')}
                  />
                  {selectedOption === 'AUTO_PAY_UPDATE_PAYMENT_PROFILE' &&
                    !isEligibleToChangePaymentMethod(account.autoPay) && (
                      <IneligibleAlert
                        message={t(
                          'AUTO_PAY_ENRROLED_INELIGIBLE_CHANGE_PAYMENT_METHOD_MESSAGE',
                        )}
                      />
                    )}
                  <FormControlLabel
                    value={'AUTO_PAY_UPDATE_DAY'}
                    control={<Radio color={'primary'} />}
                    label={t('AUTO_PAY_ENROLLED_UPDATE_DAY_OPTION')}
                  />
                  {selectedOption === 'AUTO_PAY_UPDATE_DAY' ? (
                    isPDDEligible(account.preferredDueDate) ? (
                      <Grid container spacing={2}>
                        <Grid
                          item
                          xs={12}
                          container
                          spacing={2}
                          className={classes.daySelectionArea}
                        >
                          <Grid item>
                            <Typography variant={'body1'}>
                              {t('AUTO_PAY_ENROLLED_UPDATE_DAY_OPTION_TEXT')}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <DaySelector
                              value={autoPayConfig.selectedDay}
                              onChange={day =>
                                setAutoPayConfig(cfg => ({
                                  ...cfg,
                                  selectedDay: day,
                                }))
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant={'body1'}>
                            {t('AUTO_PAY_ENROLLED_UPDATE_DAY_OPTION_NOTE')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} className={classes.actionArea}>
                          <PGEButton
                            disabled={
                              autoPayConfig.selectedDay === null ||
                              autoPayConfig.selectedDay ===
                                account?.preferredDueDate?.dueDate
                                  ?.preferredDueDate
                            }
                            onClick={() => onUpdatePayDay(autoPayConfig)}
                          >
                            {t('CHANGE')}
                          </PGEButton>
                        </Grid>
                      </Grid>
                    ) : (
                      <IneligibleAlert
                        message={t('AUTO_PAY_ENROLLED_INELIGIBLE_CHANGE')}
                      />
                    )
                  ) : null}
                  <FormControlLabel
                    value={'AUTO_PAY_CANCEL'}
                    control={<Radio color={'primary'} />}
                    label={t('AUTO_PAY_ENROLLED_CANCEL_OPTION')}
                  />
                  {selectedOption === 'AUTO_PAY_CANCEL' &&
                    isEligibleToCancel(account?.autoPay) && (
                      <Grid container spacing={2}>
                        <Grid item>
                          <Typography variant={'body1'}>
                            {richT('AUTO_PAY_ENROLLED_CANCEL_INFO')}
                          </Typography>
                          <Typography variant={'body1'}>
                            {t('AUTO_PAY_ENROLLED_NEW_CANCEL_TEXT')}
                          </Typography>
                          {Boolean(
                            isDateInNextNDays(
                              account.currentCharges?.dueDate,
                              1,
                            ),
                          ) &&
                            Number(account?.currentCharges?.amountDue) > 0 && (
                              <Typography
                                variant={'body1'}
                                className={classes.errorText}
                              >
                                <strong>
                                  {t('AUTO_PAY_CANCEL_PAYMENT_IN_PROCESS', {
                                    AMOUNT: String(
                                      account?.currentCharges?.amountDue,
                                    ),
                                    DUE_DATE: toDateString(
                                      account?.currentCharges?.dueDate,
                                    ),
                                  })}
                                </strong>
                              </Typography>
                            )}
                        </Grid>
                        <Grid
                          item
                          container
                          direction={'row'}
                          justify={'flex-end'}
                          spacing={1}
                          alignItems={'center'}
                          xs={12}
                        >
                          <Grid item xs={12} className={classes.actionArea}>
                            <PGEButton onClick={onCancel}>
                              {t('AUTO_PAY_ENROLLED_CANCEL_SUBMIT')}
                            </PGEButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  {selectedOption === 'AUTO_PAY_CANCEL' &&
                    !isEligibleToCancel(account?.autoPay) && (
                      <IneligibleAlert
                        message={t(
                          'AUTO_PAY_ENROLLED_INELIGIBLE_CANCEL_MESSAGE',
                        )}
                      />
                    )}
                </RadioGroup>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        {selectedOption === 'AUTO_PAY_UPDATE_PAYMENT_PROFILE' &&
          isEligibleToChangePaymentMethod(account.autoPay) && (
            <Grid container>
              <Grid item xs={12}>
                <Paper
                  style={{
                    justifyContent: 'flex-start',
                    padding: '0px 15px 20px 15px',
                  }}
                >
                  <Grid style={{ float: 'right', margin: '10px' }}>
                    <PaymentLimitsFees tabIndex={-1} />
                  </Grid>

                  <PaymentMethodSelector
                    selectLabel={t('SELECT_YOUR_PAYMENT_METHOD')}
                    isAutopay={true}
                    accountType={account}
                    allowedCategories={isIphoneChrome()
                                          ? account?.autoPay?.availablePaymentMethods?.filter(
                                              method => method !== PaymentCategory.Venmo,
                                            )
                                          : account?.autoPay?.availablePaymentMethods ?? undefined}
                    excludeCategories={
                      account?.accountType !== AccountType.Res
                        ? [
                            PaymentCategory.Cc,
                            PaymentCategory.Dc,
                            PaymentCategory.AmazonPay,
                            PaymentCategory.PaypalAccount,
                          ]
                        : account?.accountType === AccountType.Res &&
                          account?.paymentEligibility?.isCashOnly
                        ? [PaymentCategory.Dd]
                        : undefined
                    }
                    currentProfileInfo={
                      autoPayConfig.paymentSelector.selectedProfileInfo
                    }
                    onChange={selectedProfileInfo => {
                      setAutoPayConfig(config =>
                        mergeAutoPayConfigChanges(config, {
                          newProfileList: config.paymentSelector.newProfileList,
                          selectedProfileInfo,
                        }),
                      );
                      onUpdateAutoPayConfig &&
                        onUpdateAutoPayConfig(autoPayConfig);
                    }}
                    onNew={newProfileInfo => {
                      setAutoPayConfig(config =>
                        mergeAutoPayConfigChanges(config, {
                          newProfileList: [
                            ...config.paymentSelector.newProfileList,
                            newProfileInfo.profile,
                          ],
                          selectedProfileInfo: newProfileInfo,
                        }),
                      );
                      setAutoNext(true);
                    }}
                    onDelete={deleteProfile => {
                      setAutoPayConfig(config =>
                        mergeAutoPayConfigChanges(config, {
                          newProfileList: config.paymentSelector.newProfileList.filter(
                            p => p?.token !== deleteProfile?.profile?.token,
                          ),
                          selectedProfileInfo:
                            defaultAutoPayConfig.paymentSelector
                              .selectedProfileInfo,
                        }),
                      );
                      onUpdateAutoPayConfig &&
                        onUpdateAutoPayConfig(autoPayConfig);
                    }}
                  />
                </Paper>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.actionArea}
                style={{ marginTop: '30px' }}
              >
                <Grid item xs={12} className={classes.actionArea}>
                  <PGEButton
                    disabled={
                      !autoPayConfig?.paymentSelector?.selectedProfileInfo ||
                      autoPayConfig?.paymentSelector?.selectedProfileInfo
                        ?.profile?.token === currentPaymentProfile?.token ||
                      autoPayConfig?.paymentSelector?.selectedProfileInfo
                        ?.profile === ADD_PROFILE
                    }
                    onClick={handlePaymentProfileChange}
                  >
                    {t('UPDATE_PAYMENT_PROFILE')}
                  </PGEButton>
                </Grid>
              </Grid>
            </Grid>
          )}
      </Grid>
      {showModal && (
        <AutoPayMethodChangeDialog
          open={showModal}
          onClose={() => {
            setShowModal(false);
          }}
          account={account}
          autoPayConfig={autoPayConfig}
          defaultAutoPayConfig={defaultAutoPayConfig}
          setAutoPayConfig={setAutoPayConfig}
          onUpdateAutoPayConfig={onUpdateAutoPayConfig}
          setAutoNext={setAutoNext}
          onAutoPayConfirmSubmit={() =>
            onAutoPayConfirmSubmit?.(
              currentPaymentProfile?.type === PaymentCategory.AmazonPay
                ? true
                : false,
            )
          }
          onSubmit={handlePaymentProfileChange}
          currentPaymentProfile={currentPaymentProfile}
        />
      )}
      {showPaymentCardExpirationModal && (
        <PaymentCardExpirationModal
          open={showPaymentCardExpirationModal}
          onClose={() => {
            setShowPaymentCardExpirationModal(false);
          }}
          updateCard={() => {
            setShowPaymentCardExpirationModal(false);
            handleEditProfile(currentPaymentProfile!);
          }}
          updateProfile={() => {
            setShowPaymentCardExpirationModal(false);
            setShowModal(true);
          }}
          expiryDateAndYear={
            currentPaymentProfile?.creditCardExpiryDate?.month +
            '/' +
            currentPaymentProfile?.creditCardExpiryDate?.year
          }
        />
      )}
      {editProps && (
        <EditPaymentProfile
          open={showEdit}
          editProps={editProps}
        ></EditPaymentProfile>
      )}

      {showAmazonModal &&
        currentPaymentProfile?.type !== PaymentCategory.AmazonPay &&
        (() => {
          const amazonPayProfile = savedProfileList?.find(
            profile => profile.type === PaymentCategory.AmazonPay,
          );
          return amazonPayProfile ? (
            <PaymentProfileRemoveDialog
              open={true}
              profile={amazonPayProfile}
              setShowModal={setShowAmazonModal!}
              onDeleteProfileConfirm={() => {
                setAutoPayConfig(config =>
                  mergeAutoPayConfigChanges(config, {
                    newProfileList: config.paymentSelector.newProfileList.filter(
                      p => p?.token !== amazonPayProfile?.token,
                    ),
                    selectedProfileInfo: undefined,
                  }),
                );
                onUpdateAutoPayConfig && onUpdateAutoPayConfig(autoPayConfig);
              }}
            />
          ) : null;
        })()}
    </Grid>
  );
}
